import { BroadcastChannelLink, FramedChannel, type NetChannel } from './channel';

/** Append a transient message and return the newest `copies` messages for packet-loss recovery. */
export function redundantPacket<T>(history: T[], message: T, copies = 3): T[] {
  history.push(message);
  if (history.length > copies) history.splice(0, history.length - copies);
  return [...history];
}

/**
 * Connection setup for online co-op.
 *
 * `CloudLobbyTransport` — production: the handshake goes through the Pages
 * Function `/api/lobby` (D1-backed, see functions/api/lobby.ts); gameplay runs
 * over a WebRTC data channel directly between the host and each guest. ICE
 * candidates are gathered up front and bundled into the SDP (non-trickle), so
 * each guest needs exactly one offer and one answer — a handful of HTTP calls.
 *
 * `LocalLobbyTransport` — development (`?coop=local`): two windows of the same
 * browser talk over BroadcastChannel. No server, no WebRTC, no sign-in.
 */
export interface LobbyTransport {
  readonly kind: 'cloud' | 'local';
  /** Open a lobby; `onGuest` fires for every guest whose channel opens. Resolves with the join code. */
  host(onGuest: (channel: NetChannel, name: string) => void, onError: (message: string) => void): Promise<string>;
  /** Stop admitting guests and close the lobby. */
  stopHosting(): void;
  /** How often the host checks for new joiners (slower during a run, when only rejoins matter). */
  setPollInterval?(ms: number): void;
  join(code: string, name: string, onStatus: (text: string) => void): Promise<NetChannel>;
}

export class RtcChannel extends FramedChannel {
  private recentFast: unknown[] = [];
  constructor(private pc: RTCPeerConnection, private dc: RTCDataChannel, private fast: RTCDataChannel | null = null) {
    super();
    dc.onmessage = (e) => {
      if (typeof e.data === 'string') this.receiveRaw(e.data);
    };
    dc.onclose = () => this.closed('connection closed');
    if (fast) fast.onmessage = (e) => {
      if (typeof e.data !== 'string') return;
      try {
        const packet = JSON.parse(e.data) as { u?: unknown[] };
        for (const msg of packet.u ?? []) this.receiveRaw(JSON.stringify(msg));
      } catch { /* malformed/lost unreliable packet */ }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') this.close('connection lost');
    };
  }
  get isOpen(): boolean {
    return this.dc.readyState === 'open';
  }
  protected sendRaw(text: string): void {
    if (this.dc.readyState === 'open') this.dc.send(text);
  }
  override send(msg: unknown): void {
    const value = msg && typeof msg === 'object' ? msg as { t?: unknown; c?: unknown } : null;
    const transient = (value?.t === 'in' || value?.t === 'frame') && !value.c;
    if (!transient || !this.fast || this.fast.readyState !== 'open') {
      super.send(msg);
      return;
    }
    this.fast.send(JSON.stringify({ u: redundantPacket(this.recentFast, msg) }));
  }
  protected closeRaw(): void {
    try {
      this.fast?.close();
      this.dc.close();
      this.pc.close();
    } catch {
      /* already closed */
    }
  }
}

function waitForIce(pc: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check);
      clearTimeout(timer);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === 'complete') done();
    };
    // Whatever candidates exist by the deadline are enough to try.
    const timer = setTimeout(done, timeoutMs);
    pc.addEventListener('icegatheringstatechange', check);
  });
}

function waitForOpen(dc: RTCDataChannel, timeoutMs: number): Promise<void> {
  if (dc.readyState === 'open') return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(NAT_HELP)), timeoutMs);
    dc.addEventListener('open', () => {
      clearTimeout(timer);
      resolve();
    }, { once: true });
  });
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const NAT_HELP = 'Could not open a direct connection. Some home, school and mobile networks block peer-to-peer play; try another network (or ask the host to), or set up the optional TURN relay described in docs/MULTIPLAYER.md.';

export function randomPeerId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 20);
}

export type LobbyApi = (action: string, body?: Record<string, unknown>) => Promise<Record<string, unknown>>;

/** Calls POST /api/lobby with a Firebase ID token. */
export function lobbyApi(getToken: () => Promise<string | null>, base = ''): LobbyApi {
  return async (action, body = {}) => {
    const token = await getToken();
    if (!token) throw new Error('Sign in (a guest account is fine) to play online.');
    const response = await fetch(`${base.replace(/\/$/, '')}/api/lobby`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...body }),
    });
    let result: Record<string, unknown> = {};
    try {
      result = (await response.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON error page */
    }
    if (!response.ok) throw new Error(typeof result.error === 'string' ? result.error : `Online service unavailable (${response.status}).`);
    return result;
  };
}

export class CloudLobbyTransport implements LobbyTransport {
  readonly kind = 'cloud' as const;
  private code: string | null = null;
  private polling = false;
  private seen = new Set<string>();
  private iceServers: RTCIceServer[] = [];
  private pollMs = 1500;

  setPollInterval(ms: number): void {
    this.pollMs = ms;
  }

  constructor(private api: LobbyApi, private gameVersion: string) {}

  async host(onGuest: (channel: NetChannel, name: string) => void, onError: (message: string) => void): Promise<string> {
    const created = await this.api('create', { version: this.gameVersion });
    this.code = String(created.code);
    this.iceServers = (created.iceServers as RTCIceServer[]) ?? [];
    this.polling = true;
    void this.pollLoop(onGuest, onError);
    return this.code;
  }

  private async pollLoop(onGuest: (channel: NetChannel, name: string) => void, onError: (message: string) => void): Promise<void> {
    let failures = 0;
    while (this.polling && this.code) {
      try {
        const res = await this.api('poll-host', { code: this.code });
        failures = 0;
        for (const offer of (res.offers as { peerId: string; name: string; offer: string }[]) ?? []) {
          if (this.seen.has(offer.peerId)) continue;
          this.seen.add(offer.peerId);
          void this.accept(offer).then((channel) => onGuest(channel, offer.name)).catch((e: unknown) => console.warn('guest connection failed', e));
        }
      } catch (e) {
        if (++failures >= 5) {
          onError((e as Error).message);
          return;
        }
      }
      await sleep(this.pollMs);
    }
  }

  private async accept(offer: { peerId: string; offer: string }): Promise<NetChannel> {
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    const channels = new Map<string, RTCDataChannel>();
    const channelReady = new Promise<[RTCDataChannel, RTCDataChannel]>((resolve) => (pc.ondatachannel = (e) => {
      channels.set(e.channel.label, e.channel);
      const reliable = channels.get('chronofall');
      const fast = channels.get('chronofall-input');
      if (reliable && fast) resolve([reliable, fast]);
    }));
    try {
      await pc.setRemoteDescription({ type: 'offer', sdp: offer.offer });
      await pc.setLocalDescription(await pc.createAnswer());
      await waitForIce(pc, 4000);
      await this.api('answer', { code: this.code, peerId: offer.peerId, answer: pc.localDescription?.sdp ?? '' });
      const [dc, fast] = await Promise.race([channelReady, sleep(25000).then(() => Promise.reject(new Error(NAT_HELP)))]) as [RTCDataChannel, RTCDataChannel];
      await Promise.all([waitForOpen(dc, 20000), waitForOpen(fast, 20000)]);
      return new RtcChannel(pc, dc, fast);
    } catch (e) {
      pc.close();
      throw e;
    }
  }

  stopHosting(): void {
    this.polling = false;
    if (this.code) void this.api('close', { code: this.code }).catch(() => undefined);
  }

  async join(code: string, name: string, onStatus: (text: string) => void): Promise<NetChannel> {
    onStatus('Contacting the lobby service…');
    const ice = await this.api('ice');
    const pc = new RTCPeerConnection({ iceServers: (ice.iceServers as RTCIceServer[]) ?? [] });
    const dc = pc.createDataChannel('chronofall', { ordered: true });
    const fast = pc.createDataChannel('chronofall-input', { ordered: false, maxRetransmits: 0 });
    try {
      await pc.setLocalDescription(await pc.createOffer());
      onStatus('Finding a route to the host…');
      await waitForIce(pc, 4000);
      const peerId = randomPeerId();
      await this.api('join', { code, version: this.gameVersion, peerId, name, offer: pc.localDescription?.sdp ?? '' });
      onStatus('Waiting for the host to answer…');
      let answer: string | null = null;
      for (let i = 0; i < 40 && !answer; i++) {
        await sleep(1000);
        const res = await this.api('poll-guest', { code, peerId });
        answer = typeof res.answer === 'string' ? res.answer : null;
      }
      if (!answer) throw new Error('The host did not answer. Check the code and that the host is still in the lobby.');
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });
      onStatus('Connecting…');
      await Promise.all([waitForOpen(dc, 20000), waitForOpen(fast, 20000)]);
      return new RtcChannel(pc, dc, fast);
    } catch (e) {
      pc.close();
      throw e;
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Same-browser development transport                                          */
/* -------------------------------------------------------------------------- */

type LocalSignal = { kind: 'join'; code: string; peerId: string; name: string } | { kind: 'accept' | 'reject'; code: string; peerId: string; reason?: string };

export class LocalLobbyTransport implements LobbyTransport {
  readonly kind = 'local' as const;
  private bc: BroadcastChannel | null = null;
  private code: string | null = null;

  host(onGuest: (channel: NetChannel, name: string) => void): Promise<string> {
    const code = Array.from(crypto.getRandomValues(new Uint8Array(4)), (b) => 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'[b % 31]).join('');
    this.code = code;
    this.bc = new BroadcastChannel('chronofall-local-lobby');
    this.bc.onmessage = (e: MessageEvent<LocalSignal>) => {
      const m = e.data;
      if (m?.kind !== 'join' || m.code !== code) return;
      const channel = new BroadcastChannelLink(`chronofall-local-${code}`, 'host', m.peerId);
      this.bc?.postMessage({ kind: 'accept', code, peerId: m.peerId } satisfies LocalSignal);
      onGuest(channel, m.name);
    };
    return Promise.resolve(code);
  }

  stopHosting(): void {
    this.bc?.close();
    this.bc = null;
  }

  join(code: string, name: string, onStatus: (text: string) => void): Promise<NetChannel> {
    const peerId = randomPeerId();
    const bc = new BroadcastChannel('chronofall-local-lobby');
    onStatus('Looking for a local host window…');
    return new Promise((resolve, reject) => {
      const channel = new BroadcastChannelLink(`chronofall-local-${code}`, peerId, 'host');
      const timer = setTimeout(() => {
        bc.close();
        channel.close();
        reject(new Error('No host window with that code. Open the game in another window of this browser and host there.'));
      }, 4000);
      bc.onmessage = (e: MessageEvent<LocalSignal>) => {
        if (e.data?.kind === 'accept' && e.data.peerId === peerId) {
          clearTimeout(timer);
          bc.close();
          resolve(channel);
        }
      };
      bc.postMessage({ kind: 'join', code, peerId, name } satisfies LocalSignal);
    });
  }
}
